import React, { useState, useEffect } from 'react';
import { createComponentLogger } from '../utils/logger';

export interface ResourceFormProps<T> {
    resource: T | null;
    isEditMode: boolean;
    onSubmit: (data: T) => Promise<void>;
    onCancel: () => void;
    fields: {
        name: keyof T;
        label: string;
        type?: 'text' | 'email' | 'password' | 'number';
        required?: boolean;
        placeholder?: string;
    }[];
    title: string;
    submitLabel: string;
}

export const ResourceForm = <T extends Record<string, any>>({
    resource,
    onSubmit,
    onCancel,
    fields,
    title,
    submitLabel,
}: ResourceFormProps<T>) => {
    const [formData, setFormData] = useState<Partial<T>>({});
    const [loading, setLoading] = useState(false);

    // Initialize form data with resource values
    useEffect(() => {
        if (resource) {
            const initialData: Partial<T> = {};
            fields.forEach(field => {
                initialData[field.name] = resource[field.name];
            });
            setFormData(initialData);
        } else {
            setFormData({});
        }
    }, [resource, fields]);

    const handleInputChange = (fieldName: keyof T, value: any) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate required fields
        const missingFields = fields
            .filter(field => field.required)
            .filter(field => !formData[field.name]);
        
        if (missingFields.length > 0) {
            alert(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
            return;
        }

        setLoading(true);
        try {
            await onSubmit(formData as T);
        } catch (error) {
            const logger = createComponentLogger('ResourceForm', 'submit');
            logger.error('Form submission error', { error: error instanceof Error ? error.message : String(error) });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <h3>{title}</h3>
            <form onSubmit={handleSubmit}>
                {fields.map(field => (
                    <div key={String(field.name)} style={{ marginBottom: '1rem' }}>
                        <label>
                            {field.label}:
                            <input
                                type={field.type || 'text'}
                                value={formData[field.name] || ''}
                                name={String(field.name)}
                                onChange={(e) => handleInputChange(field.name, e.target.value)}
                                placeholder={field.placeholder}
                                required={field.required}
                                style={{ marginLeft: '0.5rem' }}
                            />
                        </label>
                    </div>
                ))}
                <div style={{ marginTop: '1rem' }}>
                    <button 
                        type="submit" 
                        disabled={loading}
                        style={{ marginRight: '0.5rem' }}
                    >
                        {loading ? 'Saving...' : submitLabel}
                    </button>
                    <button 
                        type="button" 
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}; 